import { useQuery, useMutation } from "convex/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../../convex/_generated/api";
import { getInitialContacts } from "../data/contacts-data";
import type { Id } from "../../convex/_generated/dataModel";
import type { ContactStatus } from "../data/contacts-data";

export function useContacts() {
  const contacts = useQuery(api.contacts.list);
  const [cachedContacts, setCachedContacts] = useState<typeof contacts>(undefined);
  const updateMutation = useMutation(api.contacts.update);
  const seedMutation = useMutation(api.contacts.seed);
  const seeded = useRef(false);

  useEffect(() => {
    if (contacts !== undefined) {
      setCachedContacts(contacts);
    }
  }, [contacts]);

  // Auto-seed if the contacts table is empty
  useEffect(() => {
    if (contacts !== undefined && contacts.length === 0 && !seeded.current) {
      seeded.current = true;
      const initial = getInitialContacts();
      seedMutation({
        contacts: initial.map((c) => ({
          name: c.name,
          email: c.email,
          source: c.source,
          status: c.status,
          purpose: c.purpose,
          notes: c.notes,
          dateAdded: c.dateAdded,
          platform: c.platform,
        })),
      });
    }
  }, [contacts, seedMutation]);

  const updateContact = useCallback(
    (id: Id<"contacts">, updates: { status?: ContactStatus; purpose?: string; notes?: string; email?: string; platform?: string }) => {
      updateMutation({ id, ...updates });
    },
    [updateMutation],
  );

  const resolvedContacts = contacts ?? cachedContacts ?? [];

  return {
    contacts: resolvedContacts,
    isLoading: contacts === undefined && cachedContacts === undefined,
    updateContact,
  };
}
